import { useAtom, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { MARQUE } from "@dozka/ui";
import { usePermissions } from "@/core/auth/usePermissions";
import { focusedGroupIdAtom, layoutAtom } from "@/core/state";
import { setActiveTab, visit, type Layout } from "@/shell/layout";
import type { PanelId } from "@/shell/PanelRegistry";
import { getPanelVisual } from "@/shell/PanelThumbnails";
import { KIOSK_TABS, kioskRoleFor, type KioskRole } from "@/shell/render/kioskRoles";

import { AppInstallButton } from "./AppInstallButton";
import { DemoBadge } from "./DemoBridge";
import { EcransDuBureau, ItemDuRail } from "./RailDesEcrans";
import { NotificationsTray } from "./NotificationsTray";
import { ThemePickerButton } from "./ThemePickerModal";
import { MenuParametresDuRail } from "./UserBadge";
import { RangBadge } from "./RangBadge";
import { WorkspaceSwitcher } from "./workspaces";

/**
 * Le cockpit sur desktop n'a PLUS de barre du haut ni de barre de tâches
 * (Kayla, 07-10-2026 : « on gagnerait de la place verticale s'il n'y a plus
 * de barre en haut et qu'il y a juste une barre à gauche » — d'abord pour les
 * chatteurs, puis « fais pareil pour mon cockpit admin »). Le logo, les apps,
 * le téléchargement et le profil (rouage) passent dans le rail.
 *
 * ⚠️ Dans l'app de bureau (Tauri), la barre du haut RESTE — vide : elle est
 * la poignée de la fenêtre et porte ses boutons réduire/agrandir/fermer, en
 * haut à droite. Rien d'autre : le profil, l'organisation, le rang et le
 * téléchargement vivent dans le rail, comme sur le web. Kayla, 08-10-2026,
 * après une heure sans barre : « je peux plus déplacer ma page […] remets-le
 * en haut à droite. C'est juste le truc en haut : le profil, l'organisation et
 * tout, qui servait à rien. »
 */
export function useSansBarreDuHaut(): boolean {
  const isMobile = useIsMobile();
  const dansTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
  return !isMobile && !dansTauri;
}

/**
 * Rail vertical gauche (desktop) — chrome utilitaire en icônes, façon barre
 * latérale Windows : layout (workspace), taille de l'affichage et thème —
 * alignés en BAS du rail.
 * La documentation et le canal d'actualité Telegram en sont partis le
 * 18-08-2026 : la doc publique a été supprimée, et le canal ne servait plus. La langue + l'horloge vivent dans le cluster
 * system-tray de la `BottomBar` (en bas à droite). Les contrôles applicatifs
 * (taskbar des Applications) vivent en bas au centre dans la `BottomBar`.
 *
 * Masqué sur mobile (la `BottomBar` mobile conserve ces entrées). Rendu dans
 * `ChromeLayout`, donc visible dans tous les états (login / docs / cockpit).
 *
 * z-[300] : même bande que TitleBar/BottomBar → les popovers des pickers
 * (modales layout/thème) passent au-dessus des panneaux.
 *
 * KIOSQUE (chatteur, manager) : pas de `WorkspaceSwitcher` — c'est
 * l'échappatoire layout que le kiosque verrouillé retire (layout = override
 * fixe, cf `useKioskLayout`). En échange, la langue rejoint le rail : sa
 * `BottomBar` desktop est masquée entièrement en kiosque (cf `BottomBar.tsx`),
 * donc le pendant "langue" qui vivait dans son cluster system-tray n'est plus
 * joignable sans ça.
 */
export function SideRail() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { roleSlug, permissions } = usePermissions();
  const inKiosk = kioskRoleFor(roleSlug, permissions) !== null;
  const sansBarreDuHaut = useSansBarreDuHaut();
  if (isMobile) return null;

  return (
    <nav
      data-siderail
      aria-label={t("shell.sideRail.ariaLabel")}
      className="relative z-[300] flex w-12 shrink-0 flex-col items-center gap-1 border-r border-fg/[0.07] bg-panel/60 py-3 backdrop-blur-2xl backdrop-saturate-150"
    >
      {/* Le logo du rail n'existe que là où il n'y a pas de barre du haut (le
          web) : dans l'app de bureau, c'est le petit logo de la barre (Kayla,
          08-10-2026 : « celui du bas, je voulais le retirer »). */}
      {sansBarreDuHaut && (
        <img
          src={MARQUE.icone}
          alt={MARQUE.id === "dozka" ? "Dozka" : MARQUE.nom}
          className="mb-2 h-8 w-8 shrink-0"
        />
      )}
      <DemoBadge />
      {/* Kiosque : ses écrans en icônes, en HAUT du rail (Kayla, 07-10-2026 :
          « que tout soit à gauche, qu'on garde plus d'espace en haut »). La
          bande d'onglets en haut du panneau ne les porte plus (cf TabStrip). */}
      {inKiosk && <EcransDuKiosque role={kioskRoleFor(roleSlug, permissions)!} />}
      {/* Hors kiosque : les écrans du Bureau, une icône chacun, en liste FIXE
          (cf `ecransDuBureau.ts`) — exactement le rail du kiosque, sans le
          verrou. Plus d'Explorateur, d'épingles ni de barre de tâches (Kayla,
          08-10-2026). Ils défilent si le rail est trop court. */}
      {!inKiosk && permissions !== null && permissions.size > 0 && (
        <div className="flex min-h-0 flex-col items-center overflow-y-auto">
          <EcransDuBureau />
        </div>
      )}
      {/* Le rang de la semaine : il vivait dans la barre du haut. */}
      <div className="mt-2">
        <RangBadge variant="rail" />
      </div>
      {/* Spacer → les outils restent alignés en bas du rail. */}
      <div className="flex-1" />
      {/* Cloche d'invitations en attente — ne rend rien sans invitation,
          sinon apparaît en tête du cluster avec un badge + popover accept. */}
      <NotificationsTray />

      {inKiosk ? (
        // Kiosque : ni WorkspaceSwitcher (aucun layout à choisir), ni langue
        // ni taille — les deux vivent dans le volet du Thème (Kayla,
        // 07-10-2026 : « juste l'icône Thème, et tout mettre dans le truc à
        // droite »).
        <AppInstallButton variant="rail" />
      ) : (
        <>
          <WorkspaceSwitcher variant="rail" />
          <AppInstallButton variant="rail" />
        </>
      )}
      {/* La langue a migré dans la BottomBar (cluster system-tray, à côté de
          l'horloge) — cf demande user. Sauf chatteur (ci-dessus) : sa
          BottomBar desktop est masquée, la langue vit ici à la place. */}
      {/* Taille de l'affichage — ici parce que dans Paramètres → Préférences
          personne ne la trouvait (Kayla, 20-08). Posée AU-DESSUS du thème :
          le thème garde sa place tout en bas, celle qu'on connaît par cœur. */}
      {/* La taille vit dans le volet du Thème ; elle ne garde son icône que
          là où la barre du haut existe encore (app de bureau). */}
      <ThemePickerButton variant="rail" />
      <MenuParametresDuRail />
    </nav>
  );
}

/** Où vit chaque écran du kiosque dans le layout : son groupe, son index. */
function placesDesEcrans(layout: Layout, role: KioskRole) {
  const places: { panelId: PanelId; groupId: string; index: number; actif: boolean }[] = [];
  // Personnel en TÊTE du rail (Kayla, 07-10-2026 : « personnel tout en haut,
  // discussion en deuxième, formation, documentation »). L'ordre de
  // `KIOSK_TABS` ne bouge pas : son premier onglet est celui où le rôle
  // ATTERRIT, et le chatteur atterrit toujours sur ses conversations.
  const ordre = [...KIOSK_TABS[role]].sort(
    (a, b) => Number(b === "personnel") - Number(a === "personnel"),
  );
  for (const panelId of ordre) {
    visit(layout.root, (n) => {
      if (n.kind !== "tabs") return;
      const index = n.panels.findIndex((p) => p.panelId === panelId);
      if (index < 0) return;
      places.push({ panelId, groupId: n.id, index, actif: index === n.activeIndex });
      return true;
    });
  }
  return places;
}

/**
 * Les écrans fixes du kiosque, une icône chacun — ce qu'était la bande
 * d'onglets « Conversations · Formation · Personnel · Documentation ». Un clic
 * active l'onglet dans son groupe, exactement comme le clic sur l'onglet.
 */
function EcransDuKiosque({ role }: { role: KioskRole }) {
  const { t } = useTranslation();
  const [layout, setLayout] = useAtom(layoutAtom);
  const setFocusedGroupId = useSetAtom(focusedGroupIdAtom);

  return (
    <div className="flex flex-col items-center gap-1">
      {placesDesEcrans(layout, role).map(({ panelId, groupId, index, actif }) => (
        <ItemDuRail
          key={panelId}
          titre={t(`panels.registry.${panelId}.title`)}
          Icone={getPanelVisual(panelId).icon}
          actif={actif}
          ouvert={actif}
          onClick={() => {
            setFocusedGroupId(groupId);
            setLayout((prev) => setActiveTab(prev, groupId, index));
          }}
        />
      ))}
    </div>
  );
}
